import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import {
  dimensionRowLimitSelector,
  getFirstAppliedFilter,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import {
  chartIntervalSelector,
  noBrushSelector,
  tableCompareIntervalSelector,
} from '@store/trends/birdChart/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { ERROR_DIMENSION_MAP } from '@utils/dimension/errorCodeHelper';
import useFetchGroupBy from './useFetchGroupBy';
import {
  formatData,
  formatErrorColumns,
  formatMultiErrorColumns,
  formatMultiData,
  getMetricFilterMetric,
} from '../dimension-table/utils';
import useSetErrorDimensionExport from './useSetErrorDimensionExport';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import {
  isTableCompareEnabledAtom,
  modeAtom,
  TABLE_MODES,
} from '@src/components/dimension-panel-hz/atoms/index';
import { isMetricTabActiveAtom } from '@src/components/metrics-panel-hz/atoms';
import useSort from './useSort';
import { getPercentageLocalMetricName } from '@src/utils/metrics/utilities';

function useErrorDimension({ dimension, unit, enabled = true }) {
  const filter = useSelector(getFirstAppliedFilter);
  const interval: any = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);

  const { errorLimit } = useSelector(dimensionRowLimitSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const noBrushSelected: boolean = useSelector(noBrushSelector);

  const errorCodeInfo = ERROR_DIMENSION_MAP[dimension];
  const { baseline, primary, pctMetricKey } = errorCodeInfo;

  const comparisonInterval: any = useSelector(tableCompareIntervalSelector);
  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);

  const isMetricTabActive = useAtomValue(isMetricTabActiveAtom);
  const mode = useAtomValue(modeAtom);
  const showActionColumn = !realTimeOnTrends && (isMetricTabActive || mode === TABLE_MODES.filter);

  const { sortBy, onColumnSort } = useSort({ sortByMetric: primary, dimension });

  const [searchText, onSearch] = useState('');

  const metrics = [baseline, primary];

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension,
    metrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric: primary,
    limit: errorLimit,
    realTimeOnTrends,
    noBrushSelected,
    comparisonInterval,
    isTableCompareEnabled,
  });

  let rows = [];
  let totals = {};
  let columns = [];
  let pctShareMaxValues = {};

  let metric = primary;
  if (unit === '%') {
    metric = pctMetricKey;
  }
  if (unit === '%T') {
    metric = getPercentageLocalMetricName(primary);
  }

  // TODO: improve formatting
  if ('metricFilterData' in data) {
    ({ rows, totals, pctShareMaxValues } = formatMultiData({
      data,
      sortBy,
      searchText,
      metrics: [metric],
      hideUnknownRows: true,
    }));
    columns = formatMultiErrorColumns({
      dimension,
      metric,
      errorCodeInfo,
      showActionColumn,
      showTrend: isTableCompareEnabled,
    });
  } else {
    ({ rows, totals, pctShareMaxValues } = formatData({
      data,
      sortBy,
      searchText,
      hideUnknownRows: true,
    }));
    columns = formatErrorColumns({
      dimension,
      metric,
      errorCodeInfo,
      showActionColumn,
      showTrend: isTableCompareEnabled,
    });
  }

  // count value is based on error metric (vsf, vpf), but the % toggle value for these are coming up as 100 for each row
  // for % toggle we switch to secondary metric and calculate value locally (Ended Plays, Attempts)
  // totals would be 100 in that case but for totals the error metric has valid values
  // hence this hack to swtich only totals for main and secondary filter
  try {
    const primaryPercentageMetric = getPercentageMetricByKey({
      metricKey: primary,
      defaultValue: metric,
    });
    totals[pctMetricKey] = totals[primaryPercentageMetric];
    if (metricFilter.length > 0 || isTableCompareEnabled) {
      const mfMetric = getMetricFilterMetric(primaryPercentageMetric);
      const mfPctMetric = getMetricFilterMetric(pctMetricKey);
      totals[mfPctMetric] = totals[mfMetric];
    }
  } catch (err) {
    // ignore
  }

  useSetErrorDimensionExport({ dimension, errorCodeInfo, data: rows, totals: { ...totals } });

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals: { ...totals },
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    pctShareMaxValues,
  };
}

export default useErrorDimension;
